<template>
  <ion-item
    class="input-box"
    lines="none"
    :class="[isWarningClass]"
    @click="(e: any) => emit('click', e)"
    ref="componentRef"
  >
    <ion-label
      :style="nore12Style"
      :class="{ 'is-bigger': isBigger }"
      class="absolute-label"
      >{{ label }}</ion-label
    >

    <ion-textarea
      v-if="props.multiLine"
      :style="nore12Style"
      :placeholder="placeholder"
      :value="valueCopy"
      :autofocus="autofocus"
      :readonly="readonly"
      @ionInput="ionChangeEvent"
      ref="inputComponentRef"
    />

    <!--ion-input v-else :style="nore12Style" type="text" :placeholder="placeholder" :value="valueCopy" :min="minNum"
      :autofocus="autofocus" @ionInput="ionChangeEvent" :inputmode="isForPoints ? 'numeric' : undefined" :readonly="readonly" :accept="accept" ref="inputComponentRef" /-->
    <ion-input
      v-else-if="isForPoints"
      :style="nore12Style"
      :type="type"
      :placeholder="placeholder"
      :value="valueCopy"
      :min="minNum"
      :autofocus="autofocus"
      @keydown="onlyNumbers"
      @ionInput="ionChangeEvent"
      :readonly="readonly"
      :accept="accept"
      @wheel.prevent
      ref="inputComponentRef"
    >
    </ion-input>
    <ion-input
      v-else
      :style="nore12Style"
      type="text"
      :placeholder="placeholder"
      :value="valueCopy"
      :min="minNum"
      :autofocus="autofocus"
      @ionInput="ionChangeEvent"
      :readonly="readonly"
      :accept="accept"
      ref="inputComponentRef"
    />
    <slot></slot>
    <Transition name="send">
      <div
        v-if="showSendButton"
        class="send-button"
        slot="end"
        @click="sendButtonClick"
      >
        <img :src="sendSvg" />
      </div>
    </Transition>
  </ion-item>
</template>

<script setup lang="ts">
import { IonInput, IonLabel, IonItem, IonTextarea } from "@ionic/vue";

import {
  ref,
  onMounted,
  PropType,
  computed,
  watch,
  onBeforeUnmount,
  ComponentPublicInstance,
} from "vue";
import { IFont, getFont } from "@/helpers/fonts";
import { TextFieldTypes, IonInputCustomEvent } from "@ionic/core";
import { getIcon } from "@/helpers/api";
import { onlyNumbers } from "@/globals/keyboards/keyPressed";
//import AssessmentContainer from "@/modules/a.worksheets/views/components/footer/AssessmentContainer.vue";

const props = defineProps({
  value: { type: [String, Number], default: () => "" },
  type: {
    type: String as PropType<TextFieldTypes>,
    validator(value: string) {
      return [
        "text",
        "date",
        "number",
        "password",
        "email",
        "tel",
        "url",
        "search",
        "time",
        "week",
        "month",
        "datetime-local",
        "hidden",
      ].includes(value);
    },
    default: () => "text" as TextFieldTypes,
  },
  label: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  readonly: { type: Boolean, default: () => false },
  isWarning: { type: Boolean, default: false },
  showSendButton: { type: Boolean, default: () => false },
  maxNum: { type: Number, default: () => undefined },
  autofocus: { type: Boolean, default: false },
  accept: { type: String, default: () => "*" },
  isBigger: { type: Boolean, default: false },
  minNum: { type: Number, default: () => undefined },
  multiLine: { type: Boolean, default: false },
  isForPoints: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:value",
  "click",
  "send-button-click",
  "getInputEl",
  "focusout",
]);

const ionInputEl = ref<typeof IonInput | null>(null);
const isWarningClass = computed(() => (props.isWarning ? "is-warning" : ""));

const sendSvg = ref<string | undefined>();
const nore12Style = ref<IFont>();

onMounted(async () => {
  nore12Style.value = await getFont("nore12");
  sendSvg.value = await getIcon("1610A", "send");
});

function ionChangeEvent(ev: CustomEvent) {
  const target = ev.target as HTMLInputElement | HTMLTextAreaElement | null;
  //let newValue = target?.value ?? "";

  //if (props.isForPoints) {
  //  newValue = newValue.replace(/[^0-9]/g, "");
  //}

  emit("update:value", target?.value ?? "", inputComponentRef.value?.$el);
}

watch(ionInputEl, () => {
  if (ionInputEl.value) emit("getInputEl", ionInputEl.value!.$el);
});
const valueCopy = ref(props.value as any);

watch(
  () => props.value,
  (nv) => {
    valueCopy.value = nv as any;
  },
  { immediate: true }
);

function sendButtonClick() {
  emit("send-button-click");
  emit("focusout");
}

const componentRef = ref<ComponentPublicInstance | null>(null);
const inputComponentRef = ref<ComponentPublicInstance | null>(null);

onMounted(() => {
  setTimeout(() => {
    document.addEventListener("click", handleClickOutside);
  }, 100);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

function handleClickOutside(e: MouseEvent) {
  if (
    componentRef.value &&
    !componentRef.value.$el.contains(e.target as Node)
  ) {
    emit("focusout", e);
  }
}
</script>

<style scoped>
.is-warning ion-input {
  border-color: var(--milage-alert-color);
}

.is-warning ion-label {
  color: var(--milage-alert-color);
}

ion-item {
  --background: transparent;
  margin-right: 10px;
}

ion-item::part(native) {
  height: 100%;
}

.send-button {
  margin-inline-start: 4%;
  cursor: pointer;
}

.send-button img {
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  transform: scale(1.2);
}

.input-box :deep(ion-textarea) {
  border: solid var(--milage-grey) 0.1rem;
  border-radius: var(--milage-border-radius);

  --min-height: 2.75rem;
  --max-height: 80px;

  --border-color: var(--milage-grey);
  --background: transparent;
  filter: drop-shadow(0px 0px 0.1rem rgba(0, 0, 0, 0.1));
  --padding-start: 1rem;
  --padding-end: 1rem;
  --padding-top: 1rem;
  --padding-bottom: 1rem;
}

.send-enter-active,
.send-leave-active {
  transition: all 0.3s ease-in-out;
}

.send-enter-from,
.send-leave-to {
  transform: scale(0);
}

.item-input.sc-ion-label-md-h,
.item-input .sc-ion-label-md-h {
  -ms-flex: initial;
  flex: initial;
  max-width: 200px;
  pointer-events: none;
}

.is-bigger .item-input.sc-ion-label-md-h,
.item-input .sc-ion-label-md-h {
  max-width: 250px;
}
</style>
